import React, {
  ReactElement,
  useMemo,
  useState,
} from 'react';
import { Button as MUIButton, List, Switch, TextField, Stack } from '@mui/material';
import Popover from '@mui/material/Popover';
import { debounce } from 'lodash';
import api from '../../services/api';

export const ColumnMenu = ({
                             popupState,
                             anchorEl,
                             visibleColumns,
                             sortedColumns,
                             setPopupState,
                             setVisibleColumns,
                             schema,
                             viewId,
                           }: {
  popupState: any;
  anchorEl: any;
  visibleColumns: any;
  sortedColumns: any;
  setPopupState: (value: boolean) => void;
  setVisibleColumns: (value: any) => void;
  schema: any;
  viewId?: any;
  apiRef?: any;
}): ReactElement => {
  const [filterValue, setFilterValue] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState(visibleColumns);
  const [columnsForList, setColumnsForList] = useState<any>(
    Object.keys(visibleColumns).map((item: any) => {
      return { name: item };
    })
  );

  const debouncedOnChange = debounce(handleDebouncedOnChange, 300);

  function handleDebouncedOnChange(value: string) {
    setFilterValue(value.toLowerCase());
  }

  const toggleableColumnKeys = useMemo(() => {
    return Object.keys(schema.columns).filter((columnKey: any) => {
      const column = schema.columns[columnKey];
      return !column?.hideInTableView;
    });
  }, [schema.columns]);

  const areAllVisible = useMemo(() => {
    if (!toggleableColumnKeys.length) return false;

    return toggleableColumnKeys.every((key) => {
      return !!columns[key];
    });
  }, [toggleableColumnKeys, columns]);

  const persistColumns = async (nextColumns: any) => {
    if (!viewId) return;

    const newCols = Object.keys(nextColumns)
      .filter((key) => nextColumns[key])
      .map((key) => ({ name: key }));

    setColumnsForList(newCols);

    await api.patch(`/views/${viewId}`, {
      columns: newCols,
    });
  };

  const showColumns = async (newColumn: any, show: any) => {
    setLoading(true);

    try {
      const nextColumns = { ...columns, [newColumn]: show };

      setColumns(nextColumns);
      setVisibleColumns(nextColumns);

      await persistColumns(nextColumns);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAll = async () => {
    setLoading(true);

    try {
      const shouldShowAll = !areAllVisible;

      const nextColumns = { ...columns };

      toggleableColumnKeys.forEach((key) => {
        nextColumns[key] = shouldShowAll;
      });

      setColumns(nextColumns);
      setVisibleColumns(nextColumns);

      await persistColumns(nextColumns);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover
      open={popupState}
      anchorEl={anchorEl?.current}
      onClose={() => setPopupState(false)}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <div className="p-[10px]">
        <div>
          <TextField
            autoFocus
            id="standard-basic"
            label="Find Column"
            variant="standard"
            onChange={(event) => {
              debouncedOnChange(event.target.value);
            }}
          />
        </div>

        <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
          <MUIButton
            variant="outlined"
            size="small"
            onClick={handleToggleAll}
            disabled={loading}
          >
            {areAllVisible ? 'Hide all' : 'Show all'}
          </MUIButton>
        </Stack>

        <List
          sx={{
            width: '100%',
            maxWidth: 360,
            bgcolor: 'background.paper',
            paddingY: '10px',
            maxHeight: '300px',
            overflow: 'auto',
            pointerEvents: loading ? 'none' : 'auto',
            opacity: loading ? 0.8 : 1,
          }}
        >
          {Object.keys(schema.columns).map((columnKey: any, index: number) => {
            const column = schema.columns[columnKey];

            if (
              filterValue &&
              !String(column?.name ?? column?.headerName ?? '')
                .toLowerCase()
                .includes(filterValue)
            ) {
              return null;
            }

            if (column?.hideInTableView) {
              return null;
            }

            const name = column?.name ? column.name : column.headerName;
            const id = columnKey;

            return (
              <div key={index} className="flex flex-col">
                <div className="flex flex-row items-center justify-between cursor-grabbing">
                  <div className="flex flex-row items-center justify-start">
                    <Switch
                      size="small"
                      checked={
                        visibleColumns[id] === undefined
                          ? false
                          : visibleColumns[id]
                      }
                      onChange={(event, checked) => {
                        showColumns(id, checked);
                      }}
                    />
                    <div>{name}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </List>

        <div>
          <MUIButton variant="outlined" onClick={() => setPopupState(false)}>
            Close
          </MUIButton>
        </div>
      </div>
    </Popover>
  );
};